import { FC } from 'react';

import {
  Doc as DocIcon,
  Excel as ExcelIcon,
  Generic as GenericIcon,
  Pdf as PDFIcon,
} from '@equip.health/ui';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';

import { getFileExtension } from '~/lib/util/file.util';

export const CHAT_ACCEPTED_IMAGE_EXTENSION = ['jpg', 'jpeg', 'png'];
export const CHAT_ACCEPTED_PDF_EXTENSION = ['pdf'];
export const CHAT_ACCEPTED_WORD_EXTENSION = ['doc', 'docx'];
export const CHAT_ACCEPTED_EXCEL_EXTENSION = ['xls', 'xlsx'];

export enum DocumentType {
  PDF = 'pdf',
  WORD = 'word',
  EXCEL = 'excel',
  GENERIC = 'generic',
  IMAGE = 'image',
}

export const fileExtensionToIconMapper: Record<DocumentType, FC<BaseSVG>> = {
  [DocumentType.PDF]: PDFIcon,
  [DocumentType.WORD]: DocIcon,
  [DocumentType.EXCEL]: ExcelIcon,
  [DocumentType.GENERIC]: GenericIcon,
  [DocumentType.IMAGE]: GenericIcon,
};

export const getDocumentTypeFromFileName = (fileName: string): DocumentType => {
  if (!fileName) {
    return DocumentType.GENERIC;
  }

  const fileExtension = getFileExtension(fileName.toLowerCase());

  if (CHAT_ACCEPTED_PDF_EXTENSION.includes(fileExtension)) {
    return DocumentType.PDF;
  }

  if (CHAT_ACCEPTED_WORD_EXTENSION.includes(fileExtension)) {
    return DocumentType.WORD;
  }

  if (CHAT_ACCEPTED_EXCEL_EXTENSION.includes(fileExtension)) {
    return DocumentType.EXCEL;
  }

  if (CHAT_ACCEPTED_IMAGE_EXTENSION.includes(fileExtension)) {
    return DocumentType.IMAGE;
  }

  return DocumentType.GENERIC;
};

/**
 * Extracts the document ID from the filename of a patient document attachment shared in a chat via Sendbird.
 *
 * When a chat attachment is uploaded, a UUID is appended to its filename to identify the object ID
 * in Sendbird's fileMessage, as we can't manipulate metadata. This function matches the UUID regex
 * to obtain that file ID in order to perform additional operations or queries in the database.
 *
 * @param filename - The name of the file from which to extract the UUID.
 * @returns The extracted UUID if found, otherwise an empty string.
 */

export const getPatientDocumentExternalIdFromFileName = (filename: string) => {
  if (isEmpty(filename)) {
    return '';
  }

  const result = filename.match(
    /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/gm,
  );

  const firstMatch = first(result);

  return firstMatch || '';
};
